/* Balmohk · <bm-slideshow>: pista horizontal que desliza un ancho por slide (Figma: 0,5 s ease-in-out), loop sin
   rebobinar, arrastre táctil/pointer, flechas y puntos.
   API compatible con el congelado de capturas: current, select(i, _, { animate }), suspend(). */
if (!customElements.get('bm-slideshow')) {
  /* Si la transición no avisa que terminó (pestaña oculta, transform igual), se acomoda igual */
  const SETTLE_FALLBACK = 700;
  /* Px que hay que mover antes de decidir si el gesto es horizontal (slide) o vertical (scroll) */
  const DRAG_SLOP = 8;

  class BmSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bm-hero__track');
      this.slides = [...this.querySelectorAll('.bm-hero__slide')];
      this.dots = [...this.querySelectorAll('[data-bm-dot]')];
      this._current = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      /* Posición de la pista en slides: sale de 0…total-1 sólo durante el salto del loop (-1 o total) */
      this._position = this._current;
      this._suspended = false;
      this._drag = null;
      this._dragEnd = 0;
      this._abort?.abort();
      this._abort = new AbortController();
      const { signal } = this._abort;

      this.querySelector('[data-bm-prev]')?.addEventListener('click', () => this.select(this._current - 1), { signal });
      this.querySelector('[data-bm-next]')?.addEventListener('click', () => this.select(this._current + 1), { signal });
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => this.select(Number(dot.dataset.bmDot)), { signal })
      );

      this.addEventListener('mouseenter', () => this.stopTimer(), { signal });
      this.addEventListener('mouseleave', () => this.play(), { signal });
      this.addEventListener('focusin', () => this.stopTimer(), { signal });
      this.addEventListener('focusout', () => this.play(), { signal });

      /* Swipe (QA hero-02): táctil y mouse con Pointer Events */
      this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
      this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.addEventListener('pointercancel', (event) => this.onPointerUp(event, true), { signal });
      this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      /* Soltar un arrastre encima de un botón del slide no lo abre */
      this.addEventListener(
        'click',
        (event) => {
          if (performance.now() - this._dragEnd > 400) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true, signal }
      );
      this.track?.addEventListener(
        'transitionend',
        (event) => {
          if (event.target === this.track && event.propertyName === 'transform') this.settle();
        },
        { signal }
      );

      /* Los otros slides esperan fuera de la vista, al costado: sin esto el lazy pide su foto recién al entrar */
      const warm = () =>
        this.querySelectorAll('.bm-hero__slide img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true, signal });

      this.isRtl = getComputedStyle(this).direction === 'rtl';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.render(false);
      this.play();
    }

    disconnectedCallback() {
      this.stopTimer();
      window.clearTimeout(this._settleTimer);
      this._abort?.abort();
    }

    get current() {
      return this._current;
    }

    set current(index) {
      this.select(index, undefined, { animate: false });
    }

    play() {
      if (this._suspended || this.reducedMotion || !this.hasAttribute('autoplay') || this.slides.length < 2) return;
      this.stopTimer();
      const interval = Number(this.dataset.interval) || 3000;
      this.timer = window.setInterval(() => this.select(this._current + 1), interval);
    }

    stopTimer() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    suspend() {
      this._suspended = true;
      this.stopTimer();
    }

    pause() {
      this.suspend();
    }

    /* Pista en su posición (en slides) más el arrastre en px. % del translate = ancho de la pista = un slide */
    render(animate, drag = 0) {
      if (!this.track) return;
      this.track.style.transition = animate && !this.reducedMotion ? '' : 'none';
      const sign = this.isRtl ? 1 : -1;
      this.track.style.transform = `translateX(calc(${sign * this._position * 100}% + ${drag}px))`;
    }

    /* Corre el slide `index` al lugar `slot` de la pista (un lugar antes del primero o después del último en el loop) */
    place(index, slot) {
      const slide = this.slides[index];
      if (!slide) return;
      const sign = this.isRtl ? -1 : 1;
      slide.style.transform = slot === index ? '' : `translateX(${sign * (slot - index) * 100}%)`;
    }

    /* Fin del movimiento: los slides vuelven a su lugar y la pista a la posición real, sin animar */
    settle() {
      window.clearTimeout(this._settleTimer);
      this.slides.forEach((slide) => (slide.style.transform = ''));
      this._position = this._current;
      this.render(false);
      /* Fija el salto antes de que otra llamada vuelva a animar en el mismo frame */
      void this.track?.offsetWidth;
    }

    select(index, _event, options = {}) {
      const total = this.slides.length;
      if (!total) return;
      const target = ((index % total) + total) % total;
      const animate = options.animate !== false && !this.reducedMotion && total > 1;

      window.clearTimeout(this._settleTimer);
      /* Un loop a medio camino (pista en -1 o total) se termina antes de seguir */
      if (this._position !== this._current) this.settle();

      if (animate) {
        /* Loop: de la última a la primera sigue hacia adelante (y al revés hacia atrás), no rebobina la pista */
        let position = target;
        if (index >= total && this._current === total - 1) position = total;
        else if (index < 0 && this._current === 0) position = -1;
        this.place(target, position);
        this._current = target;
        this._position = position;
        this.render(true);
        this._settleTimer = window.setTimeout(() => this.settle(), SETTLE_FALLBACK);
      } else {
        this._current = target;
        this.settle();
      }

      this.slides.forEach((slide, i) => {
        const active = i === target;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
      });
      this.dots.forEach((dot, i) => {
        if (i === target) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /** @param {PointerEvent} event */
    onPointerDown(event) {
      if (this.slides.length < 2 || !event.isPrimary || event.button !== 0) return;
      /* Flechas y puntos son sus propios botones */
      if (event.target instanceof Element && event.target.closest('button')) return;
      this._drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, moving: false, type: event.pointerType };
    }

    /** @param {PointerEvent} event */
    onPointerMove(event) {
      const drag = this._drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;

      if (!drag.moving) {
        if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) return;
        /* Gana el vertical: es scroll de la página */
        if (Math.abs(dy) > Math.abs(dx)) {
          this._drag = null;
          return;
        }
        drag.moving = true;
        this.settle();
        this.stopTimer();
        this.classList.add('is-dragging');
        try {
          this.setPointerCapture(event.pointerId);
        } catch (error) {}
      }

      drag.dx = dx;
      /* El vecino del lado hacia el que se arrastra (con dos slides es el mismo de los dos lados) */
      const total = this.slides.length;
      const forward = this.isRtl ? dx > 0 : dx < 0;
      const slot = this._current + (forward ? 1 : -1);
      this.place((slot + total) % total, slot);
      this.render(false, dx);
    }

    /**
     * @param {PointerEvent} event
     * @param {boolean} [cancelled]
     */
    onPointerUp(event, cancelled = false) {
      const drag = this._drag;
      if (!drag || event.pointerId !== drag.id) return;
      this._drag = null;
      if (!drag.moving) return;

      this.classList.remove('is-dragging');
      this._dragEnd = performance.now();
      /* Pasa de slide con un 15 % del ancho (tope 80 px); si no, vuelve a su lugar */
      const threshold = Math.min(80, this.clientWidth * 0.15);
      const forward = this.isRtl ? drag.dx > 0 : drag.dx < 0;
      if (!cancelled && Math.abs(drag.dx) >= threshold) {
        this.select(this._current + (forward ? 1 : -1));
      } else {
        this.render(true);
        this._settleTimer = window.setTimeout(() => this.settle(), SETTLE_FALLBACK);
      }
      /* Con mouse el puntero sigue encima: el avance vuelve con mouseleave */
      if (drag.type !== 'mouse') this.play();
    }
  }

  customElements.define('bm-slideshow', BmSlideshow);
}
